import BackendConnect from "./connect";
import { OwnerRatingRow } from "./types";

export interface NftRatingData {
  address: string;
  collectionAddress: string;
  name?: string;
  collectionName?: string;
  stars: number;
  indexInCollection: number;
  owner: string;
  ownerName?: string;
  imageUrl?: string;
  dataUrl: string;
  rang: number;
}

export interface CollectionRatingData {
    address: string;
    name: string;
    owner: string;
    dataUrl: string;
    itemDataUrl: string;
    previewImage: string;
    nextItemIndex: number;
    price: number;
    volume: number;
    stars: number;
    rangVolume?: number;
    rangStars?: number;
}


export interface DiapasoneNumbers {
  min?: number;
  max?: number;
}

export interface DiapasoneStrings {
  min?: string;
  max?: string;
}

export interface CollectionRatingFilters {
  owner?: string;
  visible?: boolean;
  addressSearch?: string;
  searchByName?: boolean;
  searchByDescription?: boolean;
  includeMeta?: {  
    trait_type: string;
    valueExact?: string;
    valueMin?: number;
    valueMax?: number;
  };
  itemCounts?: DiapasoneNumbers;
  itemPrice?: DiapasoneNumbers;
  itemLimit?: DiapasoneNumbers;
  itemTimeLimit?: DiapasoneStrings; 
  withWhitelist?: number;
  limitPerUser?: number;
  isSbt?: boolean; 
  dateSaved?: DiapasoneStrings; 
  orderBy: 'rang_volume' | 'rang_stars' | 'date_saved' | 'item_price' | 'item_count';
  orderDir: 'ASC' | 'DESC';
  offset: number;
  limit: number;
}

export interface NotifyInfo {
  no?: number;
  date?: string;
}

function flattenCollectionFilters(
  filters: CollectionRatingFilters
): Record<string, string | number | boolean> {
  const query: Record<string, string | number | boolean> = {};

  // --- usual fields ---
  if (filters.owner) query.owner = filters.owner;
  if (filters.visible !== undefined) query.visible = filters.visible;
  if (filters.addressSearch) query.addressSearch = filters.addressSearch;
  if (filters.searchByName !== undefined) query.searchByName = filters.searchByName;
  if (filters.searchByDescription !== undefined) query.searchByDescription = filters.searchByDescription;
  if (filters.withWhitelist !== undefined) query.withWhitelist = filters.withWhitelist;
  if (filters.limitPerUser !== undefined) query.limitPerUser = filters.limitPerUser;
  if (filters.isSbt !== undefined) query.isSbt = filters.isSbt;
  if (filters.orderBy) query.orderBy = filters.orderBy;
  if (filters.orderDir) query.orderDir = filters.orderDir;
  if (filters.offset !== undefined) query.offset = filters.offset;
  if (filters.limit !== undefined) query.limit = filters.limit;

  // --- Diapasones ---
  const setDiapasone = (prefix: string, range?: DiapasoneNumbers | DiapasoneStrings) => {
    if (!range) return;
    if (range.min !== undefined) query[`${prefix}Min`] = range.min;
    if (range.max !== undefined) query[`${prefix}Max`] = range.max;
  };

  setDiapasone('itemCounts', filters.itemCounts);
  setDiapasone('itemPrice', filters.itemPrice);
  setDiapasone('itemLimit', filters.itemLimit);
  setDiapasone('itemTimeLimit', filters.itemTimeLimit);
  setDiapasone('dateSaved', filters.dateSaved);

  // --- includeMeta  ---
  if (filters.includeMeta && Array.isArray(filters.includeMeta)) {
    filters.includeMeta.forEach((meta, i) => {
      if (meta.trait_type) query[`includeMeta[${i}][trait_type]`] = meta.trait_type;
      if (meta.valueExact !== undefined) query[`includeMeta[${i}][valueExact]`] = meta.valueExact;
      if (meta.valueMin !== undefined) query[`includeMeta[${i}][valueMin]`] = meta.valueMin;
      if (meta.valueMax !== undefined) query[`includeMeta[${i}][valueMax]`] = meta.valueMax;
    });
  }

  return query;
}

class BackendService extends BackendConnect {
  private static instance: BackendService;

  constructor(headers: Record<string, string>) {
    super(headers);
  }

  static getInstance(): BackendService {
    if (!BackendService.instance) {
      BackendService.instance = new BackendService({});
      const bearerToken = localStorage.getItem("bearerToken");
      if (bearerToken) {
        BackendService.instance.setAccessToken(bearerToken);
      }
    }
    return BackendService.instance;
  }

    /**
   * Get top nfts
   */
  public async getTopNfts(filters: {
    param: "stars" | "experience",
    limit: number;
    offset: number;
  }): Promise<NftRatingData[]> {
    const { param, limit, offset } = filters;
    return new Promise(resolve => {
      this.get(`nftrating/${param}/${limit}/${offset}`)
        .then(result => {
          resolve(result);
        })
        .catch(e => {
          console.error("getTopNfts error:", e);
          resolve([]);
        });
    });
  }

  /**
   * Best nft for owner
   */
  public async getOwnerBestNft(data: {
    owner: string;
    param: "stars" | "experience";
  }): Promise<NftRatingData | null> {
    const { owner, param } = data;
    return new Promise(resolve => {
      this.get(`ownerbestnft/${param}/${owner}`)
        .then(result => {
          resolve(result);
        })
        .catch(e => {
          console.error("getOwnerBestNft error:", e);
          resolve(null);
        });
    });
  }

  /**
   * Rang of dingle nft
   */
  public async getNftRatingData(data: {
    address: string;
    param: "stars" | "experience";
  }): Promise<NftRatingData | null> {
    const { address, param } = data;
    return new Promise(resolve => {
      this.get(`nftratingdata/${param}/${address}`)
        .then(result => {
          resolve(result);
        })
        .catch(e => {
          console.error("getNftRatingData error:", e);
          resolve(null);
        });
    });
  }
  
  // -------------------- COLLECTIONS --------------------

  /** Best collection for owner */
  public async getOwnerBestCollection(data: {
    owner: string;
    param: "stars" | "volume";
  }): Promise<CollectionRatingData | null> {
    const { owner, param } = data;
    return new Promise(resolve => {
      this.get(`ownerbestcollection/${param}/${owner}`)
        .then(result => resolve(result))
        .catch(e => {
          console.error("getOwnerBestCollection error:", e);
          resolve(null);
        });
    });
  }

  /** Rating data for a single collection */
  public async getSingleCollectionRatingData(data: {
    address: string;
  }): Promise<CollectionRatingData | null> {
    const { address } = data;
    return new Promise(resolve => {
      this.get(`collectionratingdata/${address}`)
        .then(result => resolve(result))
        .catch(e => {
          console.error("getCollectionRatingData error:", e);
          resolve(null);
        });
    });
  }

  public async getCollectionsRatingByFilters(filters: CollectionRatingFilters): Promise<CollectionRatingData[]> {
    return new Promise(resolve => {
      this.get(`collectionsrating`, {...flattenCollectionFilters(filters)})
        .then(result => resolve(result?.collections || []))
        .catch(e => {
          console.error("getCollectionRatingData error:", e);
          resolve([]);
        });
    }); 
  }

  public async getCollectionsRatingCountByFilters(filters: CollectionRatingFilters): Promise<number> {
    return new Promise(resolve => {
      this.get(`collectionsratingcount`, {...flattenCollectionFilters(filters)})
        .then(result => resolve(result?.count || 0))
        .catch(e => {
          console.error("getCollectionRatingData error:", e);
          resolve(0);
        });
    }); 
  }

  public async getOwnerRatingData (address: string): Promise<OwnerRatingRow | null> {
    return new Promise(resolve => {
      this.get(`ownerratings/${address}`)
        .then(result => resolve(result?.rating || null))
        .catch(e => {
          console.error("getCollectionRatingData error:", e);
          resolve(null);
        });
    }); 
  }

  public async getWalletLastNotifyInfo (address: string): Promise<NotifyInfo> {
    return new Promise(resolve => {
      this.get(`wallet/notify/last/${address}`)
        .then(result => resolve(result.notify || {}))
        .catch(e => {
          console.error("getCollectionRatingData error:", e);
          resolve({});
        });
    }); 
  }
  
    public async noteUserNotified (address: string, no?: number): Promise<{success: boolean}> {
    return new Promise(resolve => {
      this.get(`wallet/writenotify/${address}/${no}`)
        .then(result => resolve(result))
        .catch(e => {
          console.error("getCollectionRatingData error:", e);
          resolve({ success: false });
        });
    }); 
  }
}

export const ratingBackend = BackendService.getInstance();

